import React from 'react';
import { Text, type Descendant } from 'slate';
import { Slate, Editable, type RenderElementProps, type RenderLeafProps } from 'slate-react';
import type { SlateEditorType } from '../../../types/text';
import type { Color } from '../../../types/layout';
import { handleSlateKeyDown } from './slateKeyHandlers';
import { cx, css } from '@emotion/css';
import type { CSSInterpolation } from '@emotion/serialize';
import {
  getGlobalTextColorStyle,
  getTextColorStyleWithDisplay,
} from '@builder4/shared/utils/styleHelpers';
import { useNode } from '@craftjs/core';
import {
  createTypographyVariants,
  type TypographyVariantProps,
} from '../../../styles/typographyVariants';
import { useRenderContext } from '../../../context/RenderContext';
import {
  getHrefAttr,
  getTargetAttr,
  getRelHrefAttr,
  getPopupTargetAttr,
} from '@builder4/shared/utils/attributesHelpers';
import type { StylesConfig } from '@builder4/shared/types/styles';
interface SlateEditorProps {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  editor: SlateEditorType;
  readOnly?: boolean;
  onBlur?: () => void;
}

function textLeafStyle(
  leaf: Text,
  activeGlobalStyles: StylesConfig,
  isAppliedGlobalStyle: boolean,
  selectedStyleVariant: string | undefined,
  styles: StylesConfig | undefined,
  selectedLinkVariant: string | undefined,
) {
  let style: React.CSSProperties = {};

  // Only apply properties not handled by typography variants
  // Typography variants handle: fontFamily, fontWeight, letterSpacing, fontSize
  // Leaf node handles: textTransform, textDecoration, fontStyle, fontWeight override, and text color

  // Apply textTransform if present (not handled by typography variants)
  if (leaf.textTransform) {
    style.textTransform = leaf.textTransform as React.CSSProperties['textTransform'];
  }

  // Apply textDecoration if present (not handled by typography variants)
  if (leaf.textDecoration) {
    style.textDecoration = leaf.textDecoration;
  }

  // fontWeight and fontStyle are always applied from leaf (element-level controls)
  // This allows bold/italic to work regardless of global style attachment
  if (leaf.fontWeight) {
    style.fontWeight = leaf.fontWeight;
  }
  if (leaf.fontStyle) {
    style.fontStyle = leaf.fontStyle;
  }

  // (they're not on the container via typography variants)
  if (!isAppliedGlobalStyle) {
    style = {
      ...style,
      fontFamily: leaf.fontFamily,
      fontSize: leaf.fontSize,
      letterSpacing: `${leaf.letterSpacing}px`,
    };
  }

  // Handle text color
  if (isAppliedGlobalStyle) {
    // Check for element-level color override first
    if (leaf.color) {
      const colorStyle = getGlobalTextColorStyle(leaf.color, activeGlobalStyles.colors);
      style = { ...style, ...colorStyle };
    } else if (!selectedLinkVariant && selectedStyleVariant && styles) {
      // No override - use global style fontColor
      const [category, subVariant] = selectedStyleVariant.split('.') as [
        'heading' | 'body' | 'link',
        string,
      ];
      let fontColor: Color | string | null | undefined = null;

      if (category === 'heading') {
        const headingTag = subVariant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        fontColor = styles.typography.heading[headingTag]?.fontColor;
      } else if (category === 'body') {
        const bodySize = subVariant as 'small' | 'medium' | 'large';
        fontColor = styles.typography.body[bodySize]?.fontColor;
      }

      if (fontColor) {
        const textColorStyle = getTextColorStyleWithDisplay(
          getGlobalTextColorStyle(fontColor, styles.colors),
        );
        style = { ...style, ...textColorStyle };
      }
    }
  } else if (leaf.color) {
    const colorStyle = getGlobalTextColorStyle(leaf.color, activeGlobalStyles.colors);
    style = { ...style, ...colorStyle };
  }

  return css(style as CSSInterpolation);
}

type HeadingTag = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const HEADING_TAG_MAP: Record<string, HeadingTag> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
};

const resolveHeadingTag = (variant: string | undefined): HeadingTag => {
  if (!variant) return 'p';
  const [category, subVariant] = variant.split('.');
  if (category === 'heading' && subVariant && subVariant in HEADING_TAG_MAP) {
    return HEADING_TAG_MAP[subVariant];
  }
  return 'p';
};

const ElementRenderer = (props: RenderElementProps) => {
  const { attributes, children } = props;
  const { selectedStyleVariant } = useNode((node) => ({
    selectedStyleVariant: node.data.props.selectedStyleVariant as string | undefined,
  }));
  const Tag = resolveHeadingTag(selectedStyleVariant);
  return (
    <Tag
      style={{
        padding: '0px',
        margin: '0px',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
      }}
      {...attributes}
    >
      {children}
    </Tag>
  );
};

const LeafRenderer = (
  props: RenderLeafProps,
  textVariants: ReturnType<typeof createTypographyVariants>,
  activeGlobalStyles: StylesConfig,
) => {
  let { children } = props;
  const { leaf, attributes } = props;
  const { isAppliedGlobalStyle, selectedStyleVariant } = useNode((node) => ({
    isAppliedGlobalStyle: node.data.props.appliedGlobalStyle,
    selectedStyleVariant: node.data.props.selectedStyleVariant,
  }));
  const { styles } = useRenderContext();

  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  const textLeafStyleClass = textLeafStyle(
    leaf,
    activeGlobalStyles,
    isAppliedGlobalStyle,
    selectedStyleVariant,
    styles,
    leaf.selectedLinkVariant,
  );
  const clickEvent = leaf.clickEvent;

  const linkVariantStyleClass =
    isAppliedGlobalStyle && leaf.selectedLinkVariant
      ? textVariants({
          variant: ('link.' + leaf.selectedLinkVariant) as TypographyVariantProps['variant'],
        })
      : '';

  //currently overriding link css style props with inline styles.
  if (clickEvent) {
    const href = getHrefAttr(clickEvent);
    const target = getTargetAttr(clickEvent);
    const rel = getRelHrefAttr(clickEvent);
    const popupTarget = getPopupTargetAttr(clickEvent);
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        data-popup-target={popupTarget}
        {...attributes}
        className={cx(linkVariantStyleClass, textLeafStyleClass)}
        style={{ cursor: 'pointer' }}
      >
        <span>{children}</span>
      </a>
    );
  } else {
    return (
      <span {...attributes} className={cx(linkVariantStyleClass, textLeafStyleClass)}>
        {children}
      </span>
    );
  }
};

export const SlateEditor: React.FC<SlateEditorProps> = ({
  value,
  onChange,
  editor,
  readOnly,
  onBlur,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    handleSlateKeyDown(editor, event);
  };
  const { textVariants, styles: activeGlobalStyles } = useRenderContext();

  return (
    <Slate editor={editor} initialValue={value} onChange={onChange}>
      <Editable
        renderElement={ElementRenderer}
        renderLeaf={(props: RenderLeafProps) =>
          LeafRenderer(props, textVariants, activeGlobalStyles)
        }
        onKeyDown={readOnly ? undefined : handleKeyDown}
        onBlur={onBlur}
        placeholder="Type something..."
        spellCheck={false}
        readOnly={readOnly}
      />
    </Slate>
  );
};
